import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

function CoinDetails() {
  const { symbol } = useParams();
  const navigate = useNavigate();
  const [coinData, setCoinData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [priceHistory, setPriceHistory] = useState([]);

  useEffect(() => {
    fetchCoinDetails();
    fetchPriceHistory();
    const interval = setInterval(() => {
      fetchCoinDetails();
      fetchPriceHistory();
    }, 10000);
    return () => clearInterval(interval);
  }, [symbol]);

  const fetchCoinDetails = async () => {
    try {
      setLoading(true);
      const response = await fetch(`https://api.binance.com/api/v3/ticker/24hr?symbol=${symbol}`);
      if (!response.ok) {
        throw new Error('Failed to fetch coin details');
      }
      const data = await response.json();
      setCoinData(data);
      setError(null);
    } catch (err) {
      setError(err.message);
      console.error('Error fetching coin details:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchPriceHistory = async () => {
    try {
      const response = await fetch(`https://api.binance.com/api/v3/klines?symbol=${symbol}&interval=1h&limit=24`);
      if (response.ok) {
        const data = await response.json();
        setPriceHistory(data.map(k => ({
          time: new Date(k[0]),
          open: parseFloat(k[1]),
          high: parseFloat(k[2]),
          low: parseFloat(k[3]),
          close: parseFloat(k[4]),
          volume: parseFloat(k[5])
        })));
      }
    } catch (err) {
      console.error('Error fetching price history:', err);
    }
  };

  const formatPrice = (price) => {
    const num = parseFloat(price);
    if (num < 0.01) {
      return num.toFixed(8);
    } else if (num < 1) {
      return num.toFixed(4);
    } else {
      return num.toFixed(2);
    }
  };

  const formatNumber = (num) => {
    const value = parseFloat(num);
    if (value >= 1000000000) {
      return `${(value / 1000000000).toFixed(2)}B`;
    } else if (value >= 1000000) {
      return `${(value / 1000000).toFixed(2)}M`;
    } else if (value >= 1000) {
      return `${(value / 1000).toFixed(2)}K`;
    }
    return value.toFixed(2);
  };

  const formatVolume = (volume) => {
    const num = parseFloat(volume);
    if (num >= 1000000000) {
      return `$${(num / 1000000000).toFixed(2)}B`;
    } else if (num >= 1000000) {
      return `$${(num / 1000000).toFixed(2)}M`;
    } else if (num >= 1000) {
      return `$${(num / 1000).toFixed(2)}K`;
    }
    return `$${num.toFixed(2)}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-violet-50 via-purple-50 to-pink-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg">Loading coin details...</p>
        </div>
      </div>
    );
  }

  if (error || !coinData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-violet-50 via-purple-50 to-pink-50 flex items-center justify-center px-4">
        <div className="bg-red-50 border-2 border-red-200 rounded-xl p-8 text-center max-w-md">
          <p className="text-red-800 mb-4 font-semibold text-lg">Error: {error || 'Coin not found'}</p>
          <button
            onClick={() => navigate('/')}
            className="px-6 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:from-purple-700 hover:to-pink-700 transition-colors font-medium"
          >
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  const priceChange = parseFloat(coinData.priceChangePercent);
  const priceChangeValue = parseFloat(coinData.priceChange);

  return (
    <div className="min-h-screen bg-gradient-to-br from-violet-50 via-purple-50 to-pink-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <button
          onClick={() => navigate('/')}
          className="mb-6 bg-gradient-to-r from-purple-600 to-pink-600 text-white px-6 py-3 rounded-lg hover:from-purple-700 hover:to-pink-700 transition-colors font-medium shadow-lg flex items-center space-x-2"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          <span>Back to Home</span>
        </button>

        {/* Header */}
        <div className="bg-white rounded-2xl shadow-xl p-8 mb-8 border border-gray-100">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
            <div className="flex items-center space-x-4 mb-4 sm:mb-0">
              <h1 className="text-4xl sm:text-5xl font-bold text-gray-900">{coinData.symbol.replace('USDT', '')}</h1>
              <span className="text-sm text-gray-500 bg-gray-100 px-3 py-1 rounded-lg font-medium">{coinData.symbol}</span>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-6 space-y-4 sm:space-y-0">
            <span className="text-4xl sm:text-5xl font-bold text-gray-900">${formatPrice(coinData.lastPrice)}</span>
            <span className={`px-4 py-2 rounded-xl text-xl font-semibold inline-flex items-center space-x-2 ${
              priceChange >= 0
                ? 'bg-green-100 text-green-700'
                : 'bg-red-100 text-red-700'
            }`}>
              <span>{priceChange >= 0 ? '↑' : '↓'}</span>
              <span>{Math.abs(priceChange).toFixed(2)}%</span>
              <span className="text-base opacity-75">
                ({priceChangeValue >= 0 ? '+' : ''}{formatPrice(priceChangeValue)})
              </span>
            </span>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6 mb-8">
          <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-100 hover:shadow-xl transition-shadow">
            <div className="text-sm text-gray-600 mb-2 uppercase tracking-wide font-medium">24h High</div>
            <div className="text-2xl font-bold text-gray-900">${formatPrice(coinData.highPrice)}</div>
          </div>
          <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-100 hover:shadow-xl transition-shadow">
            <div className="text-sm text-gray-600 mb-2 uppercase tracking-wide font-medium">24h Low</div>
            <div className="text-2xl font-bold text-gray-900">${formatPrice(coinData.lowPrice)}</div>
          </div>
          <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-100 hover:shadow-xl transition-shadow">
            <div className="text-sm text-gray-600 mb-2 uppercase tracking-wide font-medium">24h Volume</div>
            <div className="text-2xl font-bold text-purple-600">{formatVolume(coinData.quoteVolume)}</div>
          </div>
          <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-100 hover:shadow-xl transition-shadow">
            <div className="text-sm text-gray-600 mb-2 uppercase tracking-wide font-medium">Base Volume</div>
            <div className="text-2xl font-bold text-gray-900">{formatNumber(coinData.volume)}</div>
          </div>
          <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-100 hover:shadow-xl transition-shadow">
            <div className="text-sm text-gray-600 mb-2 uppercase tracking-wide font-medium">Open Price</div>
            <div className="text-2xl font-bold text-gray-900">${formatPrice(coinData.openPrice)}</div>
          </div>
          <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-100 hover:shadow-xl transition-shadow">
            <div className="text-sm text-gray-600 mb-2 uppercase tracking-wide font-medium">Last Price</div>
            <div className="text-2xl font-bold text-gray-900">${formatPrice(coinData.lastPrice)}</div>
          </div>
          <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-100 hover:shadow-xl transition-shadow">
            <div className="text-sm text-gray-600 mb-2 uppercase tracking-wide font-medium">Weighted Avg</div>
            <div className="text-2xl font-bold text-gray-900">${formatPrice(coinData.weightedAvgPrice)}</div>
          </div>
          <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-100 hover:shadow-xl transition-shadow">
            <div className="text-sm text-gray-600 mb-2 uppercase tracking-wide font-medium">Price Change</div>
            <div className={`text-2xl font-bold ${
              priceChangeValue >= 0 ? 'text-green-600' : 'text-red-600'
            }`}>
              {priceChangeValue >= 0 ? '+' : ''}{formatPrice(priceChangeValue)}
            </div>
          </div>
          <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-100 hover:shadow-xl transition-shadow">
            <div className="text-sm text-gray-600 mb-2 uppercase tracking-wide font-medium">Bid Price</div>
            <div className="text-2xl font-bold text-gray-900">${formatPrice(coinData.bidPrice)}</div>
          </div>
          <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-100 hover:shadow-xl transition-shadow">
            <div className="text-sm text-gray-600 mb-2 uppercase tracking-wide font-medium">Ask Price</div>
            <div className="text-2xl font-bold text-gray-900">${formatPrice(coinData.askPrice)}</div>
          </div>
          <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-100 hover:shadow-xl transition-shadow">
            <div className="text-sm text-gray-600 mb-2 uppercase tracking-wide font-medium">Bid Qty</div>
            <div className="text-2xl font-bold text-gray-900">{formatNumber(coinData.bidQty)}</div>
          </div>
          <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-100 hover:shadow-xl transition-shadow">
            <div className="text-sm text-gray-600 mb-2 uppercase tracking-wide font-medium">Ask Qty</div>
            <div className="text-2xl font-bold text-gray-900">{formatNumber(coinData.askQty)}</div>
          </div>
          <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-100 hover:shadow-xl transition-shadow">
            <div className="text-sm text-gray-600 mb-2 uppercase tracking-wide font-medium">Trade Count</div>
            <div className="text-2xl font-bold text-gray-900">{formatNumber(coinData.count)}</div>
          </div>
        </div>

        {/* Price History */}
        {priceHistory.length > 0 && (
          <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Last 24 Hours Price History</h2>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50 border-b-2 border-gray-200">
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 uppercase tracking-wide">Time</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 uppercase tracking-wide">Open</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 uppercase tracking-wide">High</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 uppercase tracking-wide">Low</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 uppercase tracking-wide">Close</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 uppercase tracking-wide">Volume</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {priceHistory.slice(-12).reverse().map((candle, index) => (
                    <tr key={index} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3 text-sm text-gray-700">{candle.time.toLocaleTimeString()}</td>
                      <td className="px-4 py-3 text-sm text-gray-900 font-medium">${formatPrice(candle.open)}</td>
                      <td className="px-4 py-3 text-sm text-green-600 font-semibold">${formatPrice(candle.high)}</td>
                      <td className="px-4 py-3 text-sm text-red-600 font-semibold">${formatPrice(candle.low)}</td>
                      <td className="px-4 py-3 text-sm text-gray-900 font-medium">${formatPrice(candle.close)}</td>
                      <td className="px-4 py-3 text-sm text-gray-700">{formatNumber(candle.volume)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default CoinDetails;
